// PM 系統共享類型定義

export interface PMProject {
  id: string;
  organizationId: string;
  customerId: string | null;
  userId: string;
  name: string;
  description?: string;
  projectType: string;
  status: string;
  /** 暫停原因（ON_HOLD 時）：CLIENT_UNRESPONSIVE、CLIENT_REQUEST、WAITING_CLIENT、INTERNAL、OTHER */
  holdReason?: string | null;
  totalBudget?: number;
  paidAmount: number;
  pendingAmount: number;
  /** 列為呆帳的時間（null＝未列）；列入後待收款為 0，剩餘金額計入 badDebtAmount */
  badDebtAt?: string | null;
  /** 定期收費（月費合約）：每期自動建立一個分包；沒設定為 null */
  recurringAmount?: number | null;
  recurringInterval?: RecurringInterval | null;
  recurringName?: string | null;
  recurringStartDate?: string | null;
  recurringNextDate?: string | null;
  recurringEndDate?: string | null;
  badDebtAmount?: number;
  badDebtNote?: string | null;
  consultingStartDate?: string;
  consultingEndDate?: string;
  developmentStartDate?: string;
  developmentEndDate?: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
  crmQuoteId?: string;
  /** 客戶驗收連結的 token（null＝未開啟） */
  shareToken?: string | null;
  createdAt?: string;
  updatedAt?: string;
  packages?: PMPackage[];
  _count?: { packages: number };
  /** 列表 API 附帶的進度（詳情頁由任務／里程碑即時計算） */
  progress?: ProjectProgress;
}

/** 專案進度：完成任務數／任務數；沒有任務時用里程碑（basis=null 表示兩者都沒有） */
export interface ProjectProgress {
  basis: 'tasks' | 'milestones' | null;
  total: number;
  completed: number;
  overdueTasks: number;
}

// 專案分包：每包獨立報價，專案總預算 = 未取消各包金額加總
export interface PMPackage {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  orderIndex: number;
  amount: number;
  crmQuoteId?: string;
  status: string; // PENDING, IN_PROGRESS, COMPLETED, CANCELLED
  paidAmount?: number; // 由後端依里程碑付款記錄加總
  createdAt: string;
  updatedAt: string;
}

export interface PMRequirement {
  id: string;
  projectId: string;
  title: string;
  description?: string;
  priority: string;
  category?: string;
  estimatedHours?: number;
  actualHours?: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface PMMilestone {
  id: string;
  projectId: string;
  packageId?: string | null;
  name: string;
  description?: string;
  orderIndex: number;
  dueDate?: string;
  completedDate?: string;
  paymentPercentage?: number;
  paymentAmount?: number;
  status: string;
  /** 客戶從驗收連結的回覆（里程碑狀態改變時清空） */
  acceptanceStatus?: AcceptanceStatus | null;
  acceptanceBy?: string | null;
  acceptanceNote?: string | null;
  acceptanceAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AcceptanceStatus = 'ACCEPTED' | 'CHANGES_REQUESTED';

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

export interface PMTask {
  id: string;
  projectId: string;
  milestoneId?: string;
  /** 實作哪一項需求 */
  requirementId?: string | null;
  /** 檢查清單（子項目） */
  checklist?: ChecklistItem[] | null;
  title: string;
  description?: string;
  assignedTo?: string;
  estimatedHours?: number;
  actualHours?: number;
  dueDate?: string;
  completedDate?: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  /** 「我的任務」API 附上所屬專案 */
  project?: { id: string; name: string };
}

/** 任務活動：留言、建立、改狀態／負責人／到期日 */
export interface PMTaskActivity {
  id: string;
  taskId: string;
  userId: string;
  type: 'COMMENT' | 'CREATED' | 'STATUS' | 'ASSIGNEE' | 'DUE_DATE';
  body?: string | null;
  fromValue?: string | null;
  toValue?: string | null;
  createdAt: string;
}

export interface PMNotification {
  id: string;
  actorId: string | null;
  type: 'TASK_ASSIGNED' | 'TASK_COMMENT';
  title: string;
  link: string;
  readAt: string | null;
  createdAt: string;
}

export interface PMPayment {
  id: string;
  projectId: string;
  milestoneId?: string;
  packageId?: string | null;
  organizationId: string;
  amount: number;
  /** 收款差額：扣繳、手續費、折讓（沒收到但視為已結清） */
  adjustmentAmount?: number;
  adjustmentType?: 'WITHHOLDING' | 'BANK_FEE' | 'DISCOUNT' | null;
  paymentMethod: string;
  paymentDate: string;
  invoiceNumber?: string;
  receiptUrl?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/** 工時記錄（costRate 只有管理員拿得到） */
export interface PMTimeEntry {
  id: string;
  projectId: string;
  taskId: string | null;
  userId: string;
  date: string;
  hours: number;
  note: string | null;
  costRate?: number;
  createdAt: string;
  project?: { name: string };
  task?: { title: string } | null;
}

/** 專案毛利（管理員）：合約金額 − 人力成本 */
export interface ProjectProfitability {
  revenue: number;
  hours: number;
  estimatedHours: number;
  laborCost: number;
  /** 外部成本（外包、採購…）；總成本＝人力＋外部 */
  expenseCost: number;
  totalCost: number;
  costBudget: number | null;
  /** 成本預算用了幾 %（沒設預算為 null） */
  costUsage: number | null;
  margin: number;
  marginRate: number | null;
  unratedHours: number;
  members: { userId: string; hours: number; cost: number }[];
}

export type RecurringInterval = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export type ExpenseCategory = 'OUTSOURCING' | 'PURCHASE' | 'SOFTWARE' | 'TRAVEL' | 'OTHER';
export const EXPENSE_CATEGORIES: ExpenseCategory[] = ['OUTSOURCING', 'PURCHASE', 'SOFTWARE', 'TRAVEL', 'OTHER'];

/** 專案外部成本（管理員） */
export interface PMExpense {
  id: string;
  projectId: string;
  packageId: string | null;
  package: { id: string; name: string } | null;
  expenseDate: string;
  amount: number;
  category: ExpenseCategory;
  vendor: string | null;
  description: string | null;
  createdAt: string;
}

export type ChangeOrderStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** 變更單：核准後建立分包（packageId） */
export interface PMChangeOrder {
  id: string;
  projectId: string;
  title: string;
  description: string | null;
  amount: number;
  estimatedHours: number | null;
  status: ChangeOrderStatus;
  requestedBy: string;
  decidedBy: string | null;
  decisionNote: string | null;
  decidedAt: string | null;
  packageId: string | null;
  createdAt: string;
}
